<template>
    <div
        class="users-table-page text-start"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <v-container class="p-0" fluid>
            <div class="page-card">
                <div class="d-flex justify-content-between align-items-start">
                    <div>
                        <h1 class="card-title mb-2">Usuários</h1>
                        <p class="card-subtitle mb-4 mb-md-0">
                            Gerencie os usuários do sistema.
                        </p>
                    </div>
                    <a
                        class="btnd btnd--purple btnd--prepend-icon text-capitalize pr-4 pr-md-6"
                        @click="
                            () => this.$router.push('/dashboard/form-usuario')
                        "
                        title="Criar novo usuário"
                    >
                        <Icon name="plus" size="20" class="mr-0 mr-md-2" />
                        <span class="d-none d-md-block">Novo usuário</span>
                    </a>
                </div>
                <hr class="page-card__divider" />

                <v-row class="mt-1 mb-2" justify-sm="space-between">
                    <v-col cols="12" sm="7" md="5" lg="4">
                        <v-text-field
                            v-model="searchTerm"
                            label="Pesquisar usuários"
                            class="inputd"
                            variant="outlined"
                            density="compact"
                            hide-details
                            @update:model-value="onSearch"
                        >
                            <template v-slot:append-inner>
                                <Icon name="magnifyingGlass" size="20" />
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col
                        cols="12"
                        sm="5"
                        md="4"
                        lg="6"
                        style="display: flex; gap: 10px"
                    >
                        <v-select
                            v-model="statusFilter"
                            :items="statusFilterOptions"
                            item-title="text"
                            item-value="value"
                            label="Status"
                            variant="outlined"
                            class="inputd"
                            @update:modelValue="getUsersData()"
                            density="compact"
                            hide-details
                        />
                        <v-select
                            v-model="typeFilter"
                            :items="typeFilterOptions"
                            item-title="text"
                            item-value="value"
                            label="Tipo"
                            variant="outlined"
                            class="inputd"
                            @update:modelValue="getUsersData()"
                            density="compact"
                            hide-details
                        />
                    </v-col>
                </v-row>

                <div data-aos="fade-up" data-aos-duration="1500">
                    <v-table class="custom-table">
                        <thead>
                            <tr>
                                <th class="users-table__name-column">Nome</th>
                                <th class="text-center">Tipo</th>

                                <th class="text-center">Status</th>
                                <th>Telefone</th>
                                <th>Email</th>
                                <th class="text-center">Editar</th>
                                <th class="text-center">Excluir</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="user in filteredUsers" :key="user.id">
                                <td class="users-table__name-column">
                                    {{ user.name }}
                                </td>
                                <td class="text-center">
                                    {{ user.type == 3 ? 'Aluno' : 'Gestor' }}
                                </td>
                                <td class="text-center">
                                    <v-chip
                                        density="compact"
                                        :color="
                                            user.status == 1
                                                ? 'success'
                                                : 'danger'
                                        "
                                    >
                                        {{
                                            user.status && user.status == 1
                                                ? 'Ativo'
                                                : 'Inativo'
                                        }}
                                    </v-chip>
                                </td>
                                <td>
                                    {{
                                        user.phone
                                            ? user.phone
                                            : 'Não informado'
                                    }}
                                </td>
                                <td>{{ user.email }}</td>
                                <td class="text-center">
                                    <v-btn
                                        color="heading"
                                        @click="editUser(user.id)"
                                        icon
                                        variant="text"
                                        title="Editar usuário"
                                    >
                                        <Icon name="pencil" size="20" />
                                    </v-btn>
                                </td>
                                <td class="text-center">
                                    <v-btn
                                        color="danger"
                                        variant="text"
                                        @click="openDeleteModal(user.id)"
                                        icon
                                        title="Excluir usuário"
                                    >
                                        <Icon name="trash" size="20" />
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>
                <v-responsive>
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        :total-visible="5"
                        :disabled="isLoading"
                        @update:model-value="getUsersData"
                    ></v-pagination>
                </v-responsive>

                <Loader :isActive="!isLoading" />
            </div>
        </v-container>
        <v-dialog v-model="isDeleteDialogVisible" :max-width="600">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">
                        Você tem certeza que deseja excluir esse usuário?
                    </h3>
                </v-card-title>
                <v-card-subtitle>
                    Esta ação não poderá ser desfeita.
                </v-card-subtitle>
                <v-card-text>
                    <p class="mb-1">
                        Nome: <strong>{{ deletedUser.name }}</strong>
                    </p>
                    <p class="mb-1">
                        Email: <strong>{{ deletedUser.email }}</strong>
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="isDeleteDialogVisible = false"
                        >Cancelar</v-btn
                    >
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="danger"
                        variant="flat"
                        :loading="isLoading"
                        :disabled="isLoading"
                        @click="confirmDelete"
                        >Excluir</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { toast } from 'vue3-toastify';
    import { mapState } from 'vuex';
    import { debounce } from 'lodash';
    import ApiUser from '@/utils/user';
    import Loader from '@/components/Loader/loader.vue';

    export default {
        components: {
            Loader,
        },
        data() {
            return {
                usersData: [],
                usersFiltered: [],
                searchTerm: '',
                currentPage: 1,
                itemsPerPage: 10,
                totalPages: 0,
                isDeleteDialogVisible: false,
                deletedUser: null,
                isLoading: false,
                statusFilter: '',
                statusFilterOptions: [
                    { text: 'Todos', value: '' },
                    { text: 'Ativo', value: 1 },
                    { text: 'Inativo', value: 0 },
                ],
                typeFilter: '',
                typeFilterOptions: [
                    { text: 'Todos', value: '' },
                    { text: 'Aluno', value: 3 },
                    { text: 'Gestor', value: 2 },
                ],
            };
        },
        computed: {
            ...mapState(['token', 'numberIntensTable', 'websites']),
            filteredUsers() {
                // Filtro de tipo implementado aqui
                let filtered = this.usersData;

                if (this.typeFilter !== '') {
                    filtered = filtered.filter(
                        (user) => user.type == this.typeFilter
                    );
                }

                return filtered;
            },
        },
        created() {
            this.getUsersData();
        },
        methods: {
            openDeleteModal(userId) {
                this.deletedUser = this.usersData.find(
                    (user) => user.id === userId
                );
                if (!this.deletedUser) {
                    toast.error('Usuário não encontrado');
                    return;
                }
                this.isDeleteDialogVisible = true;
            },
            editUser(id) {
                this.$router.push({ path: '/dashboard/form-usuario/' + id });
            },
            async getUsersData() {
                try {
                    this.isLoading = true;
                    const response = await ApiUser.getAllUser(
                        this.token,
                        this.currentPage - 1,
                        this.itemsPerPage,
                        this.searchTerm,
                        this.statusFilter,
                        this.typeFilter
                    );
                    this.usersData = response.content;
                    this.totalPages = response.totalPages;
                } catch (err) {
                    toast.error('Erro ao carregar listagem de usuários');
                } finally {
                    this.isLoading = false;
                }
            },
            async confirmDelete() {
                if (this.deletedUser) {
                    try {
                        this.isLoading = true;
                        await ApiUser.EditUser(
                            {
                                ...this.deletedUser,
                                status: 9,
                                id: undefined,
                                photoFinal: undefined,
                                dateInsert: undefined,
                                orderStatus: undefined,
                            },
                            this.token,
                            this.deletedUser.id
                        );
                        toast.success('Usuário excluído com sucesso');
                        this.getUsersData();
                    } catch (error) {
                        toast.error('Erro ao excluir usuário:', error);
                    } finally {
                        this.isDeleteDialogVisible = false;
                        this.isLoading = false;
                        this.deletedUser = { name: '', email: '' };
                    }
                }
            },
            onSearch: debounce(function () {
                this.getUsersData();
            }, 300),
        },
    };
</script>
